import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import {
  computeExpenseLineItemTotalsUseCase,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  type ExpenseTaxSelectValue,
  isPresetTax,
  resolveTaxMode,
} from '@webapp/modules/expense/components/ExpenseTaxSelect'
import {
  ExpensePaymentTypes,
  type ExpensePaymentTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import { type ExpenseItemFormSchema } from '../ExpenseLineItemDrawer/types'

const buildExpenseTypeData = (
  values: ExpenseLineItemFormSchema
): ExpenseItemFormSchema['expenseTypeData'] => {
  switch (values.expenseType) {
    case ExpenseTypes.MEALS:
      return { type: 'MEALS', data: { tips: values.tips ?? 0 } }
    case ExpenseTypes.MILEAGE:
      return {
        type: 'MILEAGE',
        data: {
          mileageRate: values.mileageRate ?? 0,
          distance: values.distance ?? 0,
          unitOfDistance: values.unitOfDistanceStr.value as 'km' | 'mi',
        },
      }
    case ExpenseTypes.LABOUR:
      return {
        type: 'LABOUR',
        data: {
          hourlyRate: values.hourlyRate ?? 0,
          hoursOfWork: values.hoursOfWork ?? 0,
        },
      }
    case ExpenseTypes.AIRFARE:
      return {
        type: 'AIRFARE',
        data: {
          airline: values.airline,
          ticketNumber: values.ticketNumber,
          departureDate: values.departureDate
            ? values.departureDate.format('YYYY-MM-DD')
            : null,
          arrivalDate: values.arrivalDate
            ? values.arrivalDate.format('YYYY-MM-DD')
            : null,
        },
      }
    case ExpenseTypes.LODGING:
      return { type: 'LODGING', data: null }
    default:
      return { type: 'OTHER', data: null }
  }
}

const buildExpenseItemTaxMode = (
  taxTypeValue: ExpenseTaxSelectValue
): ExpenseItemFormSchema['taxMode'] => {
  if (isPresetTax(taxTypeValue)) {
    return 'saved'
  }
  if (taxTypeValue.id === '$') {
    return 'dollar'
  }
  return 'percent'
}

const buildExpenseItemTaxPercentage = (
  taxTypeValue: ExpenseTaxSelectValue,
  isManualPercent: boolean,
  values: ExpenseLineItemFormSchema
): string => {
  if (isPresetTax(taxTypeValue)) {
    return taxTypeValue.percentage
  }
  if (isManualPercent) {
    return String((values.customTaxPercentage ?? 0) / 100)
  }
  if (taxTypeValue.id === '%') {
    return String((values.taxAmount ?? 0) / 100)
  }
  return '0'
}

export const mapFormSchemaToExpenseItemFormSchema = (
  values: ExpenseLineItemFormSchema,
  item: ExpenseItem | undefined
): ExpenseItemFormSchema => {
  if (values.currency == null) {
    // Yup validation requires currency before submit, so this path only
    // fires when validation has been bypassed.
    throw new Error('ExpenseLineItemForm submitted without a currency')
  }

  const taxTypeValue = values.taxType
  const isTaxProfile = isPresetTax(taxTypeValue)
  const isTaxCustomPercent =
    !isTaxProfile &&
    taxTypeValue.id === '%' &&
    values.customTaxPercentage != null
  const isTaxOverridable = isTaxProfile || isTaxCustomPercent

  const { computedSubtotal, computedTaxAmount, computedTotal } =
    computeExpenseLineItemTotalsUseCase.execute({
      expenseType: values.expenseType,
      subtotal: values.subtotal,
      tax: resolveTaxMode({
        taxType: taxTypeValue,
        taxAmount: values.taxAmount,
        customTaxPercentage: values.customTaxPercentage,
        isOverridden: false,
      }),
      tips: values.tips ?? 0,
      mileageRate: values.mileageRate ?? 0,
      distance: values.distance ?? 0,
      hourlyRate: values.hourlyRate ?? 0,
      hoursOfWork: values.hoursOfWork ?? 0,
    })

  const overridableTaxAmount = values.isTaxAmountOverridden
    ? (values.taxAmount ?? 0)
    : computedTaxAmount
  const overridableTotal = round2(
    computedSubtotal +
      overridableTaxAmount +
      (values.expenseType === ExpenseTypes.MEALS ? (values.tips ?? 0) : 0)
  )
  const finalTaxAmount = isTaxOverridable
    ? overridableTaxAmount
    : computedTaxAmount
  const finalTotal = isTaxOverridable ? overridableTotal : computedTotal

  const taxMode = buildExpenseItemTaxMode(taxTypeValue)

  const taxName = isTaxProfile ? taxTypeValue.name : taxTypeValue.id
  const taxPercentage = buildExpenseItemTaxPercentage(
    taxTypeValue,
    isTaxCustomPercent,
    values
  )
  const taxId = isTaxProfile ? taxTypeValue.id : null
  const taxValue = isTaxCustomPercent
    ? (values.customTaxPercentage ?? 0)
    : (values.taxAmount ?? 0)

  // Reimbursable items do not collect a payment method from the UI, so any
  // stale selection is cleared and the original paymentType is preserved on
  // edit (OTHER for new items). For non-reimbursable items with no selection
  // (e.g. CASH/CHEQUE on edit, which have no matching Select option), preserve
  // the item's original paymentType rather than silently coercing to OTHER.
  const resolvedPaymentMethod = values.reimbursable
    ? null
    : (values.paymentMethod ?? null)

  const resolvedPaymentType: ExpensePaymentTypeEnum = (() => {
    if (resolvedPaymentMethod == null) {
      return item?.paymentType ?? ExpensePaymentTypes.OTHER
    }
    if (resolvedPaymentMethod.id === OTHER_PAYMENT_OPTION_ID) {
      return ExpensePaymentTypes.OTHER
    }
    return ExpensePaymentTypes.CREDIT_CARD_ASSIGNED
  })()

  return {
    id: item?.id ?? null,
    merchant: values.merchant,
    account: values.account ?? null,
    currency: values.currency,
    reimbursable: values.reimbursable,
    expenseType: values.expenseType,
    paymentType: resolvedPaymentType,
    paymentMethod: resolvedPaymentMethod,
    amount: String(finalTotal),
    subtotal: String(computedSubtotal),
    taxMode,
    taxValue: String(taxValue),
    taxAmount: String(finalTaxAmount),
    isTaxAmountOverridden: values.isTaxAmountOverridden,
    taxId,
    taxName,
    taxPercentage,
    taxIsInclusive: isTaxProfile ? taxTypeValue.isInclusive : false,
    transactionDate: values.transDate
      ? values.transDate.format('YYYY-MM-DD')
      : '',
    attachment: values.attachment,
    notes: values.notes,
    expenseTypeData: buildExpenseTypeData(values),
    mileageRatePreset:
      values.expenseType === ExpenseTypes.MILEAGE
        ? values.mileageRatePreset
        : null,
  }
}
